import { useState, useMemo, useEffect } from 'react';
import { Button } from './Button';
import { CreateGameModal } from './CreateGameModal';
import { fetchTables, type TableConfig, type CreateTableConfig } from '../lib/gameClient';

/** Cash game table row for lobby display */
export interface CashGameTable {
  id: string;
  game: string;
  type: string;
  stakes: string;
  buyInMin: number;
  buyInMax: number;
  players: number;
  seats: number;
}

function toCashGameTable(t: TableConfig): CashGameTable {
  return {
    id: t.id,
    game: 'Hold\'em',
    type: 'NL Hold\'em',
    stakes: t.stakes,
    buyInMin: t.minBuyIn / 100,
    buyInMax: t.maxBuyIn / 100,
    players: t.players ?? 0,
    seats: t.maxSeats,
  };
}

export interface LobbyProps {
  /** Current balance from backend */
  balance?: number;
  /** Username or email for display */
  userDisplayName?: string;
  onSignOut?: () => void;
  onJoinTable?: (tableId: string) => void;
  /** Create table then join (opens table view) */
  onCreateAndJoin?: (config: CreateTableConfig) => void;
  /** True while joining a table */
  joining?: boolean;
  /** True while creating a table */
  creating?: boolean;
  /** IDs of tables the user is already connected to */
  activeTables?: Set<string>;
  /** Called when user clicks Go To Table on an already-connected table */
  onGoToTable?: (tableId: string) => void;
}

export function Lobby({
  balance = 0,
  userDisplayName,
  onSignOut,
  onJoinTable,
  onCreateAndJoin,
  joining = false,
  creating = false,
  activeTables = new Set(),
  onGoToTable,
}: LobbyProps) {
  const [activeTab, setActiveTab] = useState<'cash-games'>('cash-games');
  const [buyInFilter, setBuyInFilter] = useState<string>('all');
  const [seatsFilter, setSeatsFilter] = useState<2 | 6 | 'all'>('all');
  const [tables, setTables] = useState<CashGameTable[]>([]);
  const [tablesError, setTablesError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    const refresh = () => {
      fetchTables()
        .then((ts) => setTables(ts.map(toCashGameTable)))
        .catch(() => setTablesError('Could not load tables'));
    };
    refresh();
    const interval = setInterval(refresh, 3000);
    return () => clearInterval(interval);
  }, []);

  const filteredTables = useMemo(() => {
    return tables.filter((t) => {
      if (buyInFilter !== 'all') {
        // Include tables where max buy-in is at or below the filter (inclusive).
        const limit = Number(buyInFilter);
        if (t.buyInMax > limit) return false;
      }
      if (seatsFilter !== 'all' && t.seats !== seatsFilter) return false;
      return true;
    });
  }, [tables, buyInFilter, seatsFilter]);

  return (
    <div className="min-h-full flex flex-col bg-background">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-surface/50 backdrop-blur-sm shrink-0">
        <h1 className="text-lg font-semibold text-text tracking-tight">
          NoCutPoker
        </h1>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <span className="text-text-muted text-sm">Balance</span>
            <span className="text-primary font-semibold tabular-nums">
              ${balance.toFixed(2)}
            </span>
          </div>
          {userDisplayName && (
            <span className="text-text-subtle text-sm truncate max-w-[180px]" title={userDisplayName}>
              {userDisplayName}
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Tabs */}
        <nav className="flex gap-1 px-6 py-3 border-b border-white/5 bg-surface/30">
          <button
            type="button"
            onClick={() => setActiveTab('cash-games')}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              activeTab === 'cash-games'
                ? 'bg-primary text-white'
                : 'text-text-muted hover:text-text hover:bg-white/5'
            }`}
          >
            Cash Games
          </button>
          {/* Placeholder for future tabs */}
          {/* <button type="button" className="px-5 py-2.5 rounded-xl text-sm font-medium text-text-subtle">
            Tournaments
          </button> */}
        </nav>

        {/* Cash Games content */}
        {activeTab === 'cash-games' && (
          <div className="flex-1 flex flex-col overflow-hidden px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-text-muted text-sm">Buy-in</span>
                <select
                  value={buyInFilter}
                  onChange={(e) => setBuyInFilter(e.target.value)}
                  className="rounded-lg bg-surface border border-white/10 px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                >
                  <option value="all">Any</option>
                  <option value="25">Up to $25</option>
                  <option value="50">Up to $50</option>
                  <option value="100">Up to $100</option>
                  <option value="200">Up to $200</option>
                  <option value="500">Up to $500</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-text-muted text-sm">Seats</span>
                <select
                  value={seatsFilter}
                  onChange={(e) =>
                    setSeatsFilter(
                      e.target.value === 'all'
                        ? 'all'
                        : (Number(e.target.value) as 2 | 6)
                    )
                  }
                  className="rounded-lg bg-surface border border-white/10 px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                >
                  <option value="all">Any</option>
                  <option value="2">2</option>
                  <option value="6">6</option>
                </select>
              </div>
              </div>
              <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                Create Game
              </Button>
            </div>

            <CreateGameModal
              isOpen={showCreateModal}
              onClose={() => setShowCreateModal(false)}
              onCreate={(config) => {
                onCreateAndJoin?.(config);
                setShowCreateModal(false);
              }}
              creating={creating}
            />

            {/* Table list */}
            <div className="flex-1 overflow-auto rounded-xl border border-white/5 bg-surface/30">
              <table className="w-full text-left">
                <thead className="sticky top-0 z-10 bg-surface border-b border-white/5">
                  <tr>
                    <th className="px-4 py-3 text-text-muted text-sm font-medium">
                      Game
                    </th>
                    <th className="px-4 py-3 text-text-muted text-sm font-medium">
                      Type
                    </th>
                    <th className="px-4 py-3 text-text-muted text-sm font-medium">
                      Stakes
                    </th>
                    <th className="px-4 py-3 text-text-muted text-sm font-medium">
                      Buy-in
                    </th>
                    <th className="px-4 py-3 text-text-muted text-sm font-medium">
                      Players
                    </th>
                    <th className="px-4 py-3 w-40" />
                  </tr>
                </thead>
                <tbody>
                  {tablesError ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-error text-sm">
                        {tablesError}
                      </td>
                    </tr>
                  ) : filteredTables.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-12 text-center text-text-muted text-sm"
                      >
                        No tables match your filters
                      </td>
                    </tr>
                  ) : (
                    filteredTables.map((table) => (
                      <tr
                        key={table.id}
                        className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors"
                      >
                        <td className="px-4 py-3 text-text font-medium">
                          {table.game}
                        </td>
                        <td className="px-4 py-3 text-text-muted text-sm">
                          {table.type}
                        </td>
                        <td className="px-4 py-3 text-text tabular-nums">
                          {table.stakes}
                        </td>
                        <td className="px-4 py-3 text-text-muted text-sm tabular-nums">
                          ${table.buyInMin.toFixed(0)}–${table.buyInMax.toFixed(0)}
                        </td>
                        <td className="px-4 py-3 text-text-muted text-sm">
                          {table.players}/{table.seats}
                        </td>
                        <td className="px-4 py-3">
                          {activeTables.has(table.id) ? (
                            <button
                              type="button"
                              onClick={() => onGoToTable?.(table.id)}
                              className="inline-flex items-center gap-1 whitespace-nowrap px-4 py-2 rounded-xl text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/5 transition-colors"
                            >
                              Go to Table
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                                <path d="M5 3l4 4-4 4" />
                              </svg>
                            </button>
                          ) : (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => onJoinTable?.(table.id)}
                              disabled={joining}
                            >
                              {joining ? 'Joining…' : 'Play'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
